import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'

import styles from './CodesTable.module.scss'

import { isApiError } from '@/api'
import { CodeListItem } from '@/api/generated'
import { togglePromoCodeActive } from '@/api/internal'
import Button from '@/components/Button'
import DataTable from '@/components/DataTable'
import { useToast } from '@/components/Toast/ToastContext'

interface CodesTableProps {
  campaignId: string
  codes: CodeListItem[]
  isLoading: boolean
}

const columnHelper = createColumnHelper<CodeListItem>()

const CodesTable = ({ campaignId, codes, isLoading }: CodesTableProps) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()

  const toggleMutation = useMutation({
    mutationFn: ({ codeId, isActive }: { codeId: string; isActive: boolean }) =>
      togglePromoCodeActive(codeId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promoCodes', campaignId] })
    },
    onError: (err) => {
      const message = isApiError(err) ? err.detail || 'Failed to toggle code' : 'Failed to toggle code'
      updateToast(message, 'error')
    },
  })

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: 'Code',
        cell: (info) => <span className={styles.codeString}>{info.getValue()}</span>,
        sortingFn: 'alphanumeric',
      }),
      columnHelper.accessor('is_active', {
        header: 'Status',
        cell: (info) => {
          const isActive = info.getValue()
          return (
            <span className={`${styles.activeBadge} ${isActive ? styles.activeTrue : styles.activeFalse}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          )
        },
      }),
      columnHelper.accessor('max_redemptions', {
        header: 'Max Redemptions',
        cell: (info) => info.getValue() ?? 'Unlimited',
      }),
      columnHelper.accessor('total_redemptions', {
        header: 'Used',
        sortingFn: 'basic',
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
        sortingFn: 'datetime',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const promoCode = info.row.original
          return (
            <Button
              type={promoCode.is_active ? 'destructiveLight' : 'dark'}
              onClick={() => toggleMutation.mutate({ codeId: promoCode.id, isActive: !promoCode.is_active })}
              title={promoCode.is_active ? 'Deactivate' : 'Activate'}
              isDisabled={toggleMutation.isPending}
            />
          )
        },
      }),
    ],
    [toggleMutation],
  )

  return (
    <DataTable
      data={codes}
      columns={columns}
      isLoading={isLoading}
      emptyMessage="No codes yet. Add codes to this campaign."
      loadingMessage="Loading codes..."
      ariaLabel="Promo codes"
    />
  )
}

export default CodesTable
